import type { MutableRefObject } from 'react';

import { FormTable } from '@connected/form-fields/components/form-table';
import { useMemo } from 'react';

import type { DeviceActions } from '../../distribution-board-device/hooks';
import type { DeviceFormValues } from '../../distribution-board-device/types/hook-types';

import { useResidualCurrentCircuitBreakerDevice } from '../../distribution-board-device/hooks';
import { createRCCBColumns } from './columns';

type RCCBDeviceTableProps = {
    electricalProjectVersionId: string;
    electricalProjectDistributionBoardId: string;
    enableExtendedRiso: boolean;
    currentPage?: number;
    onPageChange?: (page: number) => void;
    /**
     * Optional ref to expose device actions (create, copy, delete) to the parent.
     * Useful for placing the create button in the route's Section.Header.
     */
    actionsRef?: MutableRefObject<DeviceActions | null>;
};

export function RCCBDeviceTable({
    electricalProjectVersionId,
    electricalProjectDistributionBoardId,
    enableExtendedRiso,
    currentPage,
    onPageChange,
    actionsRef,
}: RCCBDeviceTableProps) {
    const hookResult = useResidualCurrentCircuitBreakerDevice({
        electricalProjectVersionId,
        electricalProjectDistributionBoardId,
        enableExtendedRiso,
    });

    // Expose actions to parent via ref if provided
    if (actionsRef) {
        actionsRef.current = hookResult.actions;
    }

    const rowProps = {
        electricalProjectVersionId,
        electricalProjectDistributionBoardId,
        page: currentPage ?? 1,
    };

    // Create column definitions
    const columns = useMemo(
        () => createRCCBColumns({
            form: hookResult.form,
            createPatcher: hookResult.createPatcher,
            getFieldOptions: hookResult.getFieldOptions,
            isFieldDisabled: hookResult.isFieldDisabled,
            actions: hookResult.actions,
            enableExtendedRiso,
            rowProps,
        }),
        [
            hookResult.form,
            hookResult.createPatcher,
            hookResult.getFieldOptions,
            hookResult.isFieldDisabled,
            hookResult.actions,
            enableExtendedRiso,
            rowProps,
        ],
    );

    if (hookResult.devices.length === 0) {
        return <div className="text-center py-4 text-muted-foreground">Geen apparaten gevonden</div>;
    }

    return (
        <FormTable<DeviceFormValues>
            key={hookResult.formKey}
            form={hookResult.form as any}
            fieldArrayName="devices"
            columns={columns}
            pageSize={10}
            currentPage={currentPage}
            onPageChange={onPageChange}
            emptyMessage="Geen apparaten gevonden"
            getRowKey={row => row.id}
        />
    );
}
